/* ==========================================================================
   On-screen keyboard

   For the text a till still needs without a physical keyboard: item notes,
   bill remarks, customer and item search. It types into whichever field inside
   `scopeRef` was touched last (falling back to the first one), through the same
   editField plumbing as the numeric keypad, and never takes focus itself.
   ========================================================================== */

import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Delete, ArrowUp, CornerDownLeft } from "feather-icons-react/build/IconComponents";
import { editField, isTypeable, keepFocus, pressEnter } from "./posHelpers";

const ROWS = [
  ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
  ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"],
  ["a", "s", "d", "f", "g", "h", "j", "k", "l", "@"],
  ["z", "x", "c", "v", "b", "n", "m", ",", ".", "-"],
];

const OnScreenKeyboard = ({ scopeRef, enterMode = "submit", onSubmit }) => {
  const [caps, setCaps] = useState(false);
  const lastRef = useRef(null);

  // Remember the last field touched inside the scope, so the keys keep typing
  // there even after a tap somewhere neutral.
  useEffect(() => {
    const scope = scopeRef?.current;
    if (!scope) return undefined;
    const onFocusIn = (event) => {
      if (isTypeable(event.target)) lastRef.current = event.target;
    };
    scope.addEventListener("focusin", onFocusIn);
    return () => scope.removeEventListener("focusin", onFocusIn);
  }, [scopeRef]);

  const target = () => {
    const scope = scopeRef?.current;
    const active = document.activeElement;
    if (scope && isTypeable(active) && scope.contains(active)) return active;
    // Re-entering the last field carries on from the end of what is there.
    const last = lastRef.current;
    if (last && last.isConnected && isTypeable(last)) {
      last.focus();
      try {
        last.setSelectionRange(last.value.length, last.value.length);
      } catch {
        /* not every input type has a caret */
      }
      return last;
    }
    const first = scope ? [...scope.querySelectorAll("input, textarea")].find(isTypeable) : null;
    first?.focus();
    return first || null;
  };

  const type = (text) => {
    const el = target();
    if (el) editField(el, "insert", text);
  };

  const enter = () => {
    const el = target();
    if (!el) return;
    if (enterMode === "newline" && el.tagName === "TEXTAREA") {
      editField(el, "insert", "\n");
      return;
    }
    if (onSubmit) onSubmit(el);
    else pressEnter(el);
  };

  const key = (label, onPress, className = "", aria) => (
    <button
      key={`${label}-${className}`}
      type="button"
      tabIndex={-1}
      className={`pos-kb__key ${className}`.trim()}
      onMouseDown={keepFocus}
      onClick={onPress}
      aria-label={aria}
    >
      {label}
    </button>
  );

  return (
    <div className="pos-kb" role="group" aria-label="On-screen keyboard">
      {ROWS.map((row, index) => (
        <div className="pos-kb__row" key={index}>
          {row.map((char) => {
            const shown = caps ? char.toUpperCase() : char;
            return key(shown, () => type(shown));
          })}
          {index === 0
            ? key(<Delete />, () => {
                const el = target();
                if (el) editField(el, "backspace");
              }, "pos-kb__key--fn", "Backspace")
            : null}
        </div>
      ))}
      <div className="pos-kb__row">
        {key(<ArrowUp />, () => setCaps((prev) => !prev), `pos-kb__key--fn ${caps ? "is-on" : ""}`, "Caps")}
        {key("Clear", () => {
          const el = target();
          if (el) editField(el, "clear");
        }, "pos-kb__key--fn")}
        {key("Space", () => type(" "), "pos-kb__key--space")}
        {key(
          <>
            <CornerDownLeft /> {enterMode === "newline" ? "New line" : "Enter"}
          </>,
          enter,
          "pos-kb__key--enter"
        )}
      </div>
    </div>
  );
};

OnScreenKeyboard.propTypes = {
  scopeRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  enterMode: PropTypes.oneOf(["submit", "newline"]),
  onSubmit: PropTypes.func,
};

export default OnScreenKeyboard;
